import { BoundSelectField, Css, FormLines, HasIdAndName } from "@homebound/beam";
import { required, useFormState } from "@homebound/form-state";
import { useSuspense } from "@rest-hooks/react";
import { Observer } from "mobx-react";
import { useLayoutEffect, useMemo, useRef } from "react";
import { LoadingBoundary } from "src/components/LoadingBoundary";
import { useController } from "src/hooks";
import { UnderwritingReport } from "src/routes/cma/endpoints/reports";
import {
  SaveUnderwritingSubjectPropertyEndpoint,
  UnderwritingSubjectPropertyEndpoint,
  UnderwritingSubjectPropertyInput,
} from "src/routes/cma/endpoints/UnderwritingSubjectPropertyEndpoint";
import { BlueprintDevelopmentsEndpoint } from "src/routes/cma/steps/ready-plan/v2/endpoints/BlueprintDevelopmentsEndpoint";
import {
  LoadReadyPlanOptionsForm,
  ReadyPlanOptionsForm,
} from "src/routes/cma/steps/ready-plan/v2/ReadyPlanOptionsForm";
import { FinalizedReadyPlanOptionsPreview } from "src/routes/cma/steps/ready-plan/v2/ReadyPlanOptionsPreview";
import { Maybe } from "src/utils";
import { readyPlanStepIsReadOnly } from "src/utils/reports";
import { LoadBpReadyPlanSelectFields } from "./BpReadyPlanSelectFields";
import { ConfigSelectionContext } from "./useConfigPreviewContext";

interface ReadyPlanConfigSelectionV2Props {
  report: UnderwritingReport;
}

export function ReadyPlanConfigSelectionV2({ report }: ReadyPlanConfigSelectionV2Props) {
  const { fetch } = useController();
  const isReadOnly = readyPlanStepIsReadOnly(report);

  const { uw_subj_property } = useSuspense(UnderwritingSubjectPropertyEndpoint, { dpid: report.dpid });
  const developmentsResult = useSuspense(BlueprintDevelopmentsEndpoint, { metro: report.reference_property!.metro });

  const developments: HasIdAndName[] = useMemo(
    () => developmentsResult?.map((d) => ({ id: d.id, name: d.name })) ?? [],
    [developmentsResult],
  );

  const subjectPropertyForm = useFormState<
    { devId: Maybe<string> },
    { uw_subj_property?: UnderwritingSubjectPropertyInput }
  >({
    config: { devId: { type: "value", rules: [required] } },
    init: {
      input: { uw_subj_property },
      map: ({ uw_subj_property }) => ({ devId: uw_subj_property?.development_id }),
    },
    readOnly: isReadOnly,
    autoSave: saveSubjectProperty,
  });

  // TODO: we either need to further split up the components or update the BE to delete RPs when this changes
  async function saveSubjectProperty() {
    await fetch(SaveUnderwritingSubjectPropertyEndpoint, {
      dpid: report.dpid,
      uw_subj_property: {
        development_id: subjectPropertyForm.devId.value!,
        development_name: developments.find((d) => d.id === subjectPropertyForm.devId.value)?.name!,
      },
    });
  }

  function renderForm() {
    const devId = subjectPropertyForm.devId.value;
    // A report can carry several ready plans (e.g. a primary plan plus an ADU on a product offering lot), so render
    // a config section for each. Before any plan is chosen we still render one section to pick the first plan.
    const readyPlanCount = Math.max(report.ready_plans?.length ?? 0, 1);

    return (
      <div css={Css.df.fdc.gap4.$}>
        <div css={Css.mw75.$}>
          <FormLines compact>
            {/* TODO: we should be able to get the development name from property.polygon_nearest_in_rec_geo_name_active */}
            <BoundSelectField
              options={developments}
              label={"Development"}
              field={subjectPropertyForm.devId}
              placeholder="Select a development"
            />
          </FormLines>
        </div>
        {devId &&
          [...Array(readyPlanCount).keys()].map((readyPlanIndex) => (
            <ReadyPlanConfigSection
              key={report.ready_plans?.[readyPlanIndex]?.id ?? readyPlanIndex}
              report={report}
              devId={devId}
              readyPlanIndex={readyPlanIndex}
              showDivider={readyPlanIndex > 0}
            />
          ))}
      </div>
    );
  }

  return <Observer>{() => renderForm()}</Observer>;
}

interface ReadyPlanConfigSectionProps {
  report: UnderwritingReport;
  devId: string;
  readyPlanIndex: number;
  showDivider: boolean;
}

function ReadyPlanConfigSection({ report, devId, readyPlanIndex, showDivider }: ReadyPlanConfigSectionProps) {
  const reportReadyPlan = report.ready_plans?.[readyPlanIndex];
  const isReadOnly = readyPlanStepIsReadOnly(report);

  // Each section gets its own preview element so the previews of multiple ready plans don't stack in one column
  const configPreviewRef = useRef<HTMLDivElement | null>(null);
  const configPreviewEl = useMemo(() => document.createElement("div"), []);

  useLayoutEffect(() => {
    configPreviewRef.current!.appendChild(configPreviewEl);
  }, [configPreviewEl]);

  return (
    <ConfigSelectionContext.Provider value={{ configPreviewEl }}>
      <div css={Css.df.if(showDivider).bt.bcGray300.pt4.$}>
        <div css={Css.mw75.$}>
          <FormLines compact>
            <LoadingBoundary>
              <LoadBpReadyPlanSelectFields devId={devId} report={report} readyPlanIndex={readyPlanIndex} />
            </LoadingBoundary>
            {!isReadOnly && !!reportReadyPlan && reportReadyPlan.bp_ready_plan_id && (
              <LoadReadyPlanOptionsForm
                // reset form when the ready plan changes or the product offering configuration changes
                key={`${reportReadyPlan.bp_ready_plan_id}-${reportReadyPlan.bp_poc_id}`}
                report={report}
                devId={devId}
                readyPlanIndex={readyPlanIndex}
              />
            )}
            {/* bp_ready_plan_options_json only undefined on an inactive report that never had a rp configured */}
            {isReadOnly && reportReadyPlan?.bp_ready_plan_options_json?.["optionGroups"] && (
              <ReadyPlanOptionsForm
                optionGroups={reportReadyPlan.bp_ready_plan_options_json["optionGroups"]}
                devId={devId}
                report={report}
                readyPlanIndex={readyPlanIndex}
              />
            )}
          </FormLines>
        </div>
        <div css={Css.mw25.$}>
          {/* Read-only previews come from the stored program/cost columns, which are set even when a ready plan was
              added without bp_ready_plan_options_json (e.g. an ADU synced in from the checkout lot's partitions) */}
          {isReadOnly && reportReadyPlan && <FinalizedReadyPlanOptionsPreview readyPlan={reportReadyPlan} />}
          <div ref={configPreviewRef} />
        </div>
      </div>
    </ConfigSelectionContext.Provider>
  );
}
